<script setup lang="ts">
import type { ReportImDetailProps } from '@/types/appTypes'

const props = withDefaults(defineProps<ReportImDetailProps>(), {
  title: '',
  objects: () => [],
})

const app = useNuxtApp()

let sBody = null // ボディ
let sPlace = null
let aThreeLineContent = null // 3行見出しリスト
const aRelateImg = [] as any[] // 関連画像
const refDialog = ref(null)

const oldContentMainImg = computed(() => {
  return aRelateImg[0]
})

const oldContentSubImages = computed(() => {
  return aRelateImg.slice(1)
})

const oldContentBody = (): string => {
  return sBody.replace(/\n/g, '<br>')
}

// 3行見出し追加
const addThreeLineContent = (sHeading, aThreeLineContent) => {
  if (sHeading != null) {
    aThreeLineContent.push(sHeading)
  }
}

// Youtube表示サイズ調整(旧コンテンツの場合とりあえず見栄えの為に実装)
const replaceIframeSize = (sBody) => {
  return sBody.replace(/iframe width="100%" height="223/g, 'iframe width="100%" height="406')
}

// BRをPで囲ってる場合にPを削除(旧コンテンツの場合とりあえず見栄えの為に実装)
const replaceEnter = (sBody) => {
  return sBody.replace(/<p><br><\/p>/g, '<br>')
}

// 関連記事からイベント取得
const eventItem = app.$getArticleRelationsSingle(props.item, 'event', null)
if (eventItem) {
  // イベントから表示カラム作成
  if (!sPlace) {
    sPlace = app.$getEventPlace(eventItem)
  } // 場所
}

// IMレポートから3行見出し追加
if (!aThreeLineContent && props.item && props.item.custom) {
  aThreeLineContent = []
  addThreeLineContent(props.item.custom.heading1, aThreeLineContent)
  addThreeLineContent(props.item.custom.heading2, aThreeLineContent)
  addThreeLineContent(props.item.custom.heading3, aThreeLineContent)
}

// IMレポートから関連画像追加
if (props.item && props.item.custom && props.objects) {
  app.$addSlides(
    props.item,
    'picture',
    10,
    props.objects,
    aRelateImg,
    (props.item.is_old === 1) ? null : 'l',
  )
}

// ボディ取得
if (app.$isArticleOld(props.item)) {
  // 旧コンテンツ移植
  sBody = replaceIframeSize(app.$getArticleBody(props.item))
  sBody = replaceEnter(sBody)
} else {
  // 新コンテンツ
  sBody = app.$getArticleBody(props.item)
}
</script>

<template>
  <div class="c-container">
    <div class="c-container_header">
      <div
        class="c-container_header_label"
      >
        IM <br> レポート
      </div>
      <div class="c-container_header_content">
        <div
          v-if="item && item.title"
          class="c-container_header_content_title"
        >
          {{ item.title }}
        </div>
        <div
          v-if="sPlace"
          class="c-container_header_content_location"
        >
          {{ sPlace }}
        </div>
      </div>
    </div>
    <v-divider />
    <div
      v-if="item.open_date"
      class="c-container_header_content_footer"
    >
      掲載日：{{ app.$getPeriodDayLabelNoDD(item.open_date) }}
    </div>
    <div
      v-if="item && item.custom.movie"
      class="c-container_video"
    >
      <iframe
        v-bind:src="item.custom.movie"
        frameborder="0"
        allowfullscreen
        class="c-container_video_frame"
      />
    </div>
    <div
      v-if="app.$isArticleOld(item) || (!app.$isArticleOld(item) && aThreeLineContent.length > 0)"
      class="c-container_listTags"
    >
      <div class="c-container_listTags_contents">
        <div v-if="app.$isArticleOld(item)">
          <div
            v-if="item && item.custom && item.custom.main_title"
            class="c-container_listTags_contents_title"
          >
            {{ item.custom.main_title }}
          </div>
          <div
            v-if="item && item.custom && item.custom.sub_title"
            class="c-container_listTags_contents_panel_old"
          >
            <div
              v-html="app.$decodeHtmlEntities(item.custom.sub_title)"
            />
          </div>
        </div>
        <div v-else-if="!app.$isArticleOld(item) && aThreeLineContent.length > 0">
          <div
            class="c-container_listTags_contents_panel_new"
          >
            <div
              v-for="(record, index) in aThreeLineContent"
              v-bind:key="index"
              class="c-container_listTags_contents_panel_new_description"
            >
              <div class="c-container_listTags_contents_panel_new_description_iconWrap">
                <v-icon class="c-container_listTags_contents_panel_new_description_iconWrap_icon">
                  mdi-square-medium
                </v-icon>
              </div>
              <div class="c-container_listTags_contents_panel_new_description_text c-clampLines is-clampline2">
                {{ record }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="c-container_link">
      <Clip
        v-bind:mode="true"
        v-bind:dspcnt="true"
        v-bind:article-type-slug="item.article_type_slug"
        v-bind:article-slug="item.article_slug"
        v-bind:count="item.user_favorite_count"
      />
      <ShareButtons
        v-bind:title="title"
      />
    </div>

    <template v-if="app.$isArticleOld(item)">
      <div class="c-oldContent">
        <div
          v-if="aRelateImg.length > 0"
          class="c-oldContent_mainImg"
        >
          <CommonImage
            v-if="oldContentMainImg.oImg"
            v-bind:src="oldContentMainImg.oImg"
            error-src="/images/noimage.svg"
            object-fit="contain"
          />
          <div
            v-if="oldContentMainImg.sCaption"
            class="c-oldContent_mainImg_caption"
            v-text="oldContentMainImg.sCaption"
          />
        </div>
        <div class="c-oldContent_body">
          <div
            v-if="oldContentSubImages.length > 0"
            class="c-oldContent_side"
          >
            <div class="c-oldContent_sideImageContainer">
              <div
                v-for="(record, index) in oldContentSubImages"
                v-bind:key="index"
                class="c-oldContent_sideImage"
              >
                <CommonImage
                  v-if="record.oImg"
                  v-bind:src="record.oImg"
                  error-src="/images/noimage.svg"
                  object-fit="contain"
                />
                <div
                  v-if="record.sCaption"
                  class="c-oldContent_sideImage_note"
                >
                  {{ record.sCaption }}
                </div>
              </div>
            </div>
          </div>
          <div class="c-oldContent_main">
            <div
              v-if="oldContentBody"
              class="c-container_subContent is-oldContent"
            >
              <div
                class="c-container_subContent_text l-blank-icon-none"
                v-html="oldContentBody()"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div
        v-if="sBody"
        class="c-container_subContent"
      >
        <div
          class="c-container_subContent_text l-blank-icon-none"
          v-html="sBody"
        />
      </div>
    </template>

    <div
      v-if="!app.$isArticleOld(item) && aRelateImg.length > 0"
      class="c-container_relateImages"
    >
      <div class="c-container_relateImages_row">
        <div
          v-for="(record, index) in aRelateImg"
          v-bind:key="index"
          class="c-container_relateImages_row_panel"
        >
          <CommonImage
            v-if="record.oImg"
            v-bind:src="record.oImg"
            class="c-container_relateImages_row_panel_image"
            error-src="/images/noimage.svg"
            object-fit="contain"
            aspect-ratio="4 / 3"
            v-on:click="refDialog.onClickOpen(record.oImg)"
          />

          <div
            v-if="record.sCaption"
            class="c-container_relateImages_row_panel_imgNote"
          >
            {{ record.sCaption }}
          </div>
        </div>
      </div>
    </div>
    <div class="c-info">
      <ReportInfo
        v-bind:item="item"
      />
    </div>

    <DialogImg
      ref="refDialog"
    />
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive";

.c-container {
  &_header {
    display: flex;
    margin: 10px 0 26px;

    @include responsive.mq_down(sp) {
      margin: 0 0 26px;
    }

    &_label {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100px;
      min-width: 100px;
      max-width: 100px;
      height: 100px;
      padding: 29px 16px;
      background-color: var(--color_primary_link);
      color: var(--g_color_text_white);
      font-family: var(--g_font_family_NotoSansCJKjp);
      font-size: 16px;
      font-weight: bold;
      text-align: center;

      @include responsive.mq_down(sp) {
        width: 80px;
        min-width: 80px;
        max-width: 80px;
        height: 80px;
        padding: 20px 10px;
        font-size: 14px;
      }
    }

    &_content {
      display: flex;
      flex-direction: column;
      justify-content: center;
      margin: 2px 0 4px 20px;

      @include responsive.mq_down(sp) {
        flex: 1;
        margin: 2px 0 4px 10px;
      }

      &_title {
        margin-bottom: 8px;
        color: var(--color_text_base);
        font-family: var(--g_font_family_NotoSansCJKjp);
        font-size: 26px;
        line-height: 1.2;

        @include responsive.mq_down(sp) {
          margin: 0 0 10px;
          font-size: 20px;
          line-height: 1.4;
        }
      }

      &_location {
        margin-top: 6px;
        margin-left: 2px;
        color: var(--color_text_base);
        font-family: var(--g_font_family_NotoSansCJKjp);
        font-size: 14px;
        font-weight: bold;

        @include responsive.mq_down(sp) {
          margin: 0;
        }
      }

      &_footer {
        display: flex;
        justify-content: flex-end;
        margin: 14px 0 18px;
        color: var(--g_color_text_626262);
        font-family: var(--g_font_family_NotoSansCJKjp);
        font-size: 13px;
        line-height: 1.846;
      }
    }
  }

  &_video {
    position: relative;
    width: 722px;
    height: 0;
    padding-bottom: 56.25%;

    @include responsive.mq_down(sp) {
      width: 100%;
    }

    &_frame {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  &_listTags {
    width: 722px;
    margin-top: 32px;
    background-color: var(--color_background_whiteblue);

    @include responsive.mq_down(sp) {
      width: 100%;
    }

    &_contents {
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      height: 100%;

      // padding: 11px 27px 20px 27px;
      padding: 15px 15px 9px;

      /*
      &_title {
        padding-left: 2px;
        font-family: var(--g_font_family_NotoSansCJKjp);
        font-size: 18px;
        font-weight: 500;
        line-height: 1.333;
      }

      &_panel {
        padding: 3px 0 0 12px;

        &_description {
          position: relative;
          margin: 10px 0 10px 3px;
          font-family: var(--g_font_family_NotoSansCJKjp);
          font-size: 16px;
          line-height: 1.5;

          &_iconWrap {
            position: absolute;

            &_icon {
              color: var(--color_primary_link);
            }
          }

          &_text {
            padding: 2px 0 0 31px;
            font-weight: bold;
          }
        }
      }
      */

      &_title {
        font-family: var(--g_font_family_NotoSansCJKjp);
        font-size: 18px;
        font-weight: 500;
        font-weight: bold;
        line-height: 1.333;
      }

      &_panel_old {
        padding: 8px 0 0;
        font-size: 14px;
        line-height: 1.5;
      }

      &_panel_new {
        // padding: 3px 0 0 0;
        padding: 0;

        &_description {
          position: relative;

          // margin: 10px 0 10px 0;
          margin: 0 0 10px;
          font-family: var(--g_font_family_NotoSansCJKjp);
          font-size: 15px;
          line-height: 1.3;

          &_iconWrap {
            position: absolute;

            &_icon {
              color: var(--color_primary_link);
            }
          }

          &_icon {
            color: var(--color_primary_link);
          }

          &_text {
            padding-left: 24px;
          }
        }
      }
    }
  }

  &_link {
    display: flex;
    justify-content: center;
    margin-top: 35px;

    @include responsive.mq_down(sp) {
      flex-direction: column;
      align-items: center;
    }
  }

  &_subContent {
    margin-top: 50px;

    @include responsive.mq_down(sp) {
      margin-top: 25px;
    }

    &.is-oldContent {
      margin-top: 0;

      &:deep(iframe) {
        margin: 16px 0;
      }

      &:deep(table) {
        td {
          padding: 10px;
        }

        td[width="100%"] {
          width: auto;
        }
      }

      &:deep(table[bordercolor="#d6d6d6"]) {
        border: 1px solid #d6d6d6;
      }
    }

    &_text {
      // transform: rotate(0.03deg);
      // font-family: var(--g_font_family_MPuls);
      font-family: var(--g_font_family_NotoSansCJKjp);
      font-size: 16px;
      line-height: 1.8;
    }
  }

  &_relateImages {
    // margin-top: 60px;

    &_row {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;

      @include responsive.mq_down(sp) {
        margin-left: -10px;
      }

      &_panel {
        width: 340px;
        margin: 40px 0 -6px;

        @include responsive.mq_down(sp) {
          flex-basis: calc(50% - 10px);
          max-width: calc(50% - 10px);
          margin: 10px 0 0 10px;
        }

        &_image {
          width: 100%;
          height: auto;
          background-color: #ebebeb;

          // @include responsive.mq_down(sp) {
          //   height: calc(50vw - 40px);
          // }
        }

        &_imgNote {
          margin-top: 3px;

          // transform: rotate(0.03deg);
          // font-family: var(--g_font_family_MPuls);
          font-family: var(--g_font_family_NotoSansCJKjp);
          font-size: 12px;
          line-height: 1.5;
        }
      }
    }
  }
}

.c-oldContent {
  margin-top: 50px;

  &_mainImg {
    margin-bottom: 20px;

    &_caption {
      margin-top: 5px;
      color: #6d2f41;
      font-size: 14px;
    }
  }

  &_body {
    display: flex;
    flex-wrap: wrap;

    @include responsive.mq_down(sp) {
      flex-direction: column-reverse;
    }
  }

  &_side {
    flex: 0 0 280px;
    flex-basis: 280px;
    max-width: 280px;
    padding-right: 30px;

    @include responsive.mq_down(sp) {
      flex-basis: 100%;
      max-width: 100%;
      margin-top: 40px;
      padding: 0;
    }
  }

  &_sideImageContainer {
    @include responsive.mq_down(sp) {
      display: flex;
      flex-wrap: wrap;
      margin-top: -10px;
      margin-left: -10px;
    }
  }

  &_sideImage {
    margin-top: 20px;
    color: #6d2f41;
    font-size: 14px;

    .extended-image {
      height: auto !important;
    }

    @include responsive.mq_down(sp) {
      flex-basis: calc(50% - 10px);
      max-width: calc(50% - 10px);
      margin: 10px 0 0 10px;
    }

    &:first-of-type {
      margin-top: 0;

      @include responsive.mq_down(sp) {
        margin-top: 10px;
      }
    }

    &_note {
      margin-top: 5px;
    }
  }

  &_main {
    flex: 1;
  }
}

.c-info {
  margin-top: 82px;
  margin-bottom: 100px;
}
</style>
